/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';

import { AutocompleteContainer, AutocompleteInput, AutocompletePopper } from './Base';

interface Props {
  value: string;
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: any | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

const timeZones = Intl.supportedValuesOf('timeZone');
const timeZonesWithFormattedOffsets = () =>
  timeZones
    .map((timeZone) => {
      const date = new Date();

      try {
        const offsetMinutes = Math.round(
          date.getTimezoneOffset() -
            (date.getTime() - new Date(date.toLocaleString('en-US', { timeZone })).getTime()) / 60000
        );
        return { timeZone, offset: offsetMinutes };
      } catch (error) {
        return { timeZone, offset: NaN };
      }
    })
    .sort((a, b) => a.offset - b.offset)
    .map(({ timeZone, offset }) => {
      const hours = Math.floor(Math.abs(offset) / 60)
        .toString()
        .padStart(2, '0');
      const minutes = Math.abs(offset % 60)
        .toString()
        .padStart(2, '0');
      const sign = offset === 0 ? '' : offset > 0 ? '+' : '-';
      const formattedOffset = `${sign}${hours}:${minutes}`;

      return {
        timeZone,
        offset: formattedOffset,
      };
    });

export const TimeZones: React.FC<Props> = ({ value, onChange, disabled, placeholder }) => {
  const autocompleteRef = useRef(null);

  const timeZones = timeZonesWithFormattedOffsets();
  const labels = timeZones.map(({ timeZone, offset }) => ({
    label: `${timeZone} (${offset})`,
    value: timeZone,
  }));

  const [open, setOpen] = useState<boolean>(false);

  const handleChange = (event: React.SyntheticEvent<Element, Event>, newValue: any | null) => {
    if (!newValue) return;
    onChange(event, newValue);
    setOpen(false);
    (document?.activeElement as HTMLElement)?.blur();
  };

  return (
    <AutocompleteContainer open={open}>
      <Autocomplete
        value={labels.find((item) => item?.value === value) || null}
        ref={autocompleteRef}
        onChange={handleChange}
        disabled={disabled}
        PopperComponent={AutocompletePopper}
        autoHighlight
        options={labels}
        renderOption={(props, option) => (
          <li key={option.value} {...props}>
            {option.label}
          </li>
        )}
        disablePortal
        getOptionLabel={(option) => option?.label || ''}
        renderInput={(params) => (
          <AutocompleteInput
            open={open}
            ref={params.InputProps.ref}
            inputProps={params.inputProps}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
          />
        )}
      />
    </AutocompleteContainer>
  );
};
